/**
 * The paper, generated and sent.
 *
 * Three rules this panel exists to enforce, each of which is a way a contract
 * goes wrong that no amount of care in the document itself prevents:
 *
 * 1. NOTHING IS SENT UNSEEN. A contract is not a notification. Preview opens
 *    the filled document, and it is the same text the PDF is made from.
 * 2. A DOCUMENT THAT CANNOT BE FILLED IS NOT OFFERED. The row says which blank
 *    is missing and who has to fill it, instead of a send button that produces
 *    an agreement saying "the State of {{COMPANY_STATE}}".
 * 3. NO SEND BUTTON WITHOUT DOCUSIGN. If it is not connected, the panel says
 *    how to connect it. A control that cannot work is worse than no control.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { saveFile } from './ImportPage';
import page from '../../shared/Page.module.css';
import fields from '../../shared/Form.module.css';
import { ROLE_LABEL } from './onboarding/messageFor';
import { useJump } from './onboarding/jump';
import { GroupCard } from '../../shared/GroupCard';

interface DocumentRow {
  key: string;
  label: string;
  detail: string;
  signed: boolean;
  routine: boolean;
  recipientRole: string;
  recipientEmail: string | null;
  paperworkKey: string | null;
  /** Other documents that go out in the same envelope. */
  sendsWith?: string[];
  /** What the envelope is called: "Order Form and Subscription Agreement". */
  envelopeLabel?: string;
  ready: boolean;
  missingFacts: string[];
  missingValues: string[];
  missingRecipient: string | null;
}

interface Envelope {
  envelopeId: string;
  document: string;
  documents?: string[];
  label: string;
  state: string;
  recipientEmail: string | null;
  sentAt: string | null;
  completedAt: string | null;
  sentByEmail: string | null;
}

const isLive = (e: Envelope) => e.state === 'sent' || e.state === 'delivered';
const carries = (e: Envelope, key: string) => (e.documents ?? [e.document]).includes(key);

/** An unsigned envelope carrying this document, if there is one. Mirrors the server's sendGuard.ts. */
export function liveEnvelopeOf(envelopes: Envelope[], key: string): Envelope | null {
  return envelopes.find((e) => isLive(e) && carries(e, key)) ?? null;
}

/**
 * Where a document stands: its NEWEST envelope, plus any older copy still
 * open. The row used to report any open envelope at all, so a contract signed
 * on Oct 3 read "Out for signature" because a stale Sep 30 copy was never voided.
 */
export function documentStanding(envelopes: Envelope[], key: string) {
  const mine = envelopes.filter((e) => carries(e, key))
    .sort((a, b) => String(b.sentAt ?? '').localeCompare(String(a.sentAt ?? '')));
  const newest = mine[0] ?? null;
  const staleOpen = mine.slice(1).filter(isLive);
  return { newest, staleOpen };
}

interface Pack {
  orgId: string;
  documents: DocumentRow[];
  signing: {
    configured: boolean;
    missing: string[];
    environment: string | null;
    renderConfigured: boolean;
  };
  companyFacts: { key: string; help: string }[];
  envelopes: Envelope[];
}

interface Preview {
  /** Every document on screen, in envelope order. More than one is a bundle. */
  documents?: string[];
  document: string;
  label: string;
  /** The same HTML the PDF is made from. */
  html: string;
  markdown: string;
  ready: boolean;
  missingFacts: string[];
  missingValues: string[];
  recipientEmail: string | null;
  recipientRole: string;
  pdfBase64: string | null;
  fileName: string;
}

const STATE_LABEL: Record<string, string> = {
  sent: 'Sent, not opened yet',
  delivered: 'Opened',
  completed: 'Signed by everyone',
  declined: 'Declined',
  voided: 'Voided',
};

/* Applied to an `.itemMeta` span, so the TEXT tones. The badge ones would
   paint a wash behind the sentence. */
const STATE_TONE: Record<string, string | undefined> = {
  completed: page.okText,
  declined: page.dangerText,
  voided: page.warnText,
};

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null;

/** A missing placeholder, in the words somebody can act on. */
const readableBlank = (name: string) => name
  .toLowerCase()
  .replace(/^company_/, 'our ')
  .replace(/^customer_/, "the customer's ")
  .replace(/^signatory_/, "the signatory's ")
  .replace(/^champion_/, "the champion's ")
  .replace(/^billing_/, "the billing contact's ")
  .replace(/^sysadmin_/, "the System Admin's ")
  .replace(/_/g, ' ');

export function ContractsPanel({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  /** On the onboarding page, a missing recipient links to Contacts. */
  const jumpTo = useJump();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const pack = useQuery({
    queryKey: ['contract-pack', orgId],
    queryFn: () => call<{ orgId: string }, Pack>('sysAdminGetContractPack', { orgId }),
    /*
     * One retry, because the first call of the day is a cold start.
     *
     * This panel's function carries the heaviest import graph in the codebase
     * and is called rarely, so it is almost always cold. The client gives up at
     * 30 seconds and reports "Could not reach the server", which is both wrong
     * and unactionable — the server was reachable and busy starting. The second
     * attempt lands on a warm instance and answers in under a second.
     */
    retry: 1,
    retryDelay: 1000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['contract-pack', orgId] });
    void queryClient.invalidateQueries({ queryKey: ['relationship', orgId] });
    void queryClient.invalidateQueries({ queryKey: ['go-live'] });
  };

  /*
   * PREVIEW RETRIES; SENDING DOES NOT.
   *
   * These functions are opened rarely, so they are almost always cold, and a
   * cold start here was measured at 73 seconds against a client that gives up
   * at 30. The first attempt of the day failed and the second, a few seconds
   * later, took 0.24s — which is what "it worked when I clicked again" means.
   *
   * Reading a document is idempotent, so retrying is free. SENDING IS NOT:
   * a retry that arrives after a request the client merely stopped waiting for
   * would put a second envelope in front of the customer. `send` therefore
   * has no retry, and never should.
   */
  const show = useMutation({
    mutationFn: (document: string) =>
      call<{ orgId: string; document: string }, Preview>('sysAdminPreviewContractDoc', { orgId, document }),
    onSuccess: (result) => setPreview(result),
    retry: 1,
    retryDelay: 1000,
  });

  /** The literal artifact, for when the rendered page is not proof enough. */
  const download = useMutation({
    retry: 1,
    retryDelay: 1000,
    mutationFn: (document: string) =>
      call<{ orgId: string; document: string; pdf: boolean }, Preview>(
        'sysAdminPreviewContractDoc', { orgId, document, pdf: true },
      ),
    onSuccess: (result) => {
      if (result.pdfBase64) saveFile({ fileName: result.fileName, base64: result.pdfBase64 }, 'application/pdf');
    },
  });

  const send = useMutation({
    mutationFn: (document: string) => call('sysAdminSendContractDoc', { orgId, document }),
    onSuccess: () => { setConfirming(null); setPreview(null); refresh(); },
  });

  // Asking DocuSign where an envelope got to changes nothing, so it may retry.
  const recheck = useMutation({
    mutationFn: (envelopeId: string) => call('sysAdminRefreshContractDoc', { orgId, envelopeId }),
    onSuccess: refresh,
    retry: 1,
    retryDelay: 1000,
  });

  /**
   * Open the signed copy. The tab is opened on the click itself, then pointed
   * at the link once it arrives: a tab opened after an await is a pop-up, and
   * browsers block those.
   */
  const viewSigned = useMutation({
    mutationFn: async (envelopeId: string) => {
      const tab = window.open('', '_blank');
      try {
        const r = await call<{ orgId: string; envelopeId: string }, { url: string }>(
          'sysAdminGetSignedContract', { orgId, envelopeId });
        if (tab) tab.location.href = r.url; else window.location.href = r.url;
      } catch (e) {
        tab?.close();
        throw e;
      }
    },
  });

  const [showVoided, setShowVoided] = useState(false);
  const allEnvelopes = pack.data?.envelopes ?? [];
  const voidedCount = allEnvelopes.filter((e) => e.state === 'voided').length;
  const shownEnvelopes = showVoided ? allEnvelopes : allEnvelopes.filter((e) => e.state !== 'voided');

  /** Void a copy nobody should sign: DocuSign emails the recipient the reason. */
  const [voiding, setVoiding] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const voidOne = useMutation({
    mutationFn: (envelopeId: string) =>
      call('sysAdminVoidContractEnvelope', { orgId, envelopeId, reason: voidReason.trim() }),
    onSuccess: () => { setVoiding(null); setVoidReason(''); refresh(); },
  });

  const failure = [show.error, send.error, recheck.error, download.error, voidOne.error, viewSigned.error].find(Boolean);
  const busy = send.isPending || show.isPending || recheck.isPending || download.isPending || voidOne.isPending;
  const signing = pack.data?.signing;
  const facts = pack.data?.companyFacts ?? [];

  return (
    <section aria-label="Contracts">
      <GroupCard id="contracts-title" title="Contracts and signatures">
      <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-4)' }}>
        Generated from the documents in the repository and sent for signature through DocuSign.
        Paper goes to the signatory, who is the person able to bind the organization — not to the
        champion.
      </p>

      {failure && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          {isCallFailure(failure) ? failure.message
            : failure instanceof Error ? failure.message : 'That did not work.'}
        </div>
      )}

      {/* What is stopping everything, before the per-document detail. */}
      {facts.length > 0 && (
        <div className={`${page.notice} ${page.noticeWarn}`}>
          <strong>Nothing can be sent yet.</strong> Four facts appear in every contract and only
          you can supply them. Set them in <code>functions/src/contracts/companyFacts.ts</code>:
          <ul className={page.list} style={{ marginTop: 'var(--sp-2)' }}>
            {facts.map((fact) => (
              <li key={fact.key} className={page.itemMeta}>
                <strong>{readableBlank(fact.key)}</strong> — {fact.help}
              </li>
            ))}
          </ul>
        </div>
      )}

      {signing && !signing.configured && (
        <div className={`${page.notice} ${page.noticeInfo}`}>
          <strong>DocuSign is not connected.</strong> Documents can be previewed, but nothing can be
          sent for signature. Run <code>node scripts/docusign-setup.mjs</code> from the repository —
          it stores the credentials and prints the two values to paste into DocuSign, plus the
          consent link that has to be opened once.
          {signing.missing.length > 0 && (
            <div className={page.itemMeta} style={{ marginTop: 'var(--sp-1)' }}>
              Missing: {signing.missing.join(', ')}.
            </div>
          )}
        </div>
      )}

      {signing?.configured && signing.environment === 'demo' && (
        <div className={`${page.notice} ${page.noticeWarn}`}>
          <strong>DocuSign is in sandbox mode.</strong> Envelopes are watermarked and are not legally
          binding. Re-run the setup script with <code>--production</code> before sending a real
          contract.
        </div>
      )}

      {signing && !signing.renderConfigured && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          <strong>No PDF can be generated.</strong> The document conversion service is not
          configured, so <code>GOTENBERG_URL</code> is missing from the functions environment.
        </div>
      )}

      {pack.isLoading && <p className={page.itemMeta}>Reading the record…</p>}
      {pack.error && (
        <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
          Could not read the contract pack.{' '}
          {isCallFailure(pack.error) ? pack.error.message : ''}
        </div>
      )}

      <ul className={page.list} aria-label="Documents">
        {(pack.data?.documents ?? []).map((doc) => {
          const blanks = [...doc.missingFacts, ...doc.missingValues];
          return (
            <li key={doc.key} className={page.listItem}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className={page.itemName}>
                  {doc.label}
                  {!doc.routine && (
                    <span className={page.itemMeta} style={{ fontWeight: 400 }}> · only if asked</span>
                  )}
                  {!doc.signed && (
                    <span className={page.itemMeta} style={{ fontWeight: 400 }}> · nobody signs it</span>
                  )}
                </div>
                <div className={page.itemMeta}>{doc.detail}</div>
                {doc.sendsWith && doc.sendsWith.length > 0 && (
                  <div className={page.itemMeta}>
                    Sent together with the {doc.sendsWith.join(' and ')}, in one envelope —
                    one signature covers both.
                  </div>
                )}
                <div className={page.itemMeta}>
                  {doc.missingRecipient ? (
                    <span className={page.warnText}>
                      {/* Named as the role is on Contacts, with the way there:
                          "No security named" left nobody knowing where security is set. */}
                      No {(ROLE_LABEL[doc.missingRecipient] ?? doc.missingRecipient).toLowerCase()} contact,
                      so there is nobody to send it to.{' '}
                      {jumpTo ? (
                        <button type="button" className={page.linkBtn}
                          onClick={() => jumpTo.jump({ stage: 'contacts', prepare: `newContact:${doc.missingRecipient}`, target: 'person-name' })}>
                          Add the {(ROLE_LABEL[doc.missingRecipient] ?? doc.missingRecipient).toLowerCase()} contact
                        </button>
                      ) : `Add one on the Contacts stage, with the ${ROLE_LABEL[doc.missingRecipient] ?? doc.missingRecipient} role ticked.`}
                    </span>
                  ) : (
                    <>Goes to the {doc.recipientRole} · {doc.recipientEmail}</>
                  )}
                </div>
                {blanks.length > 0 && (
                  <div className={`${page.itemMeta} ${page.warnText}`}>
                    Not ready: no {blanks.map(readableBlank).join(', ')}.
                  </div>
                )}
              </div>

              <div className={page.actions} style={{ flexShrink: 0 }}>
                <button type="button" className={page.btn} disabled={busy}
                  onClick={() => show.mutate(doc.key)}>
                  {show.isPending && show.variables === doc.key ? 'Opening…' : 'Preview'}
                </button>
                {/* No send button at all when it could not work. */}
                {doc.signed && signing?.configured && signing.renderConfigured && (() => {
                  /*
                   * NEVER A SECOND ENVELOPE. The server refuses one while an
                   * envelope for this bundle is unsigned, and while another send
                   * of it is running; the screen says so instead of offering a
                   * button that a faint disabled style made look clickable.
                   */
                  const { newest, staleOpen } = documentStanding(pack.data?.envelopes ?? [], doc.key);
                  const stale = staleOpen.length > 0 && (
                    <span className={`${page.itemMeta} ${page.warnText}`} style={{ display: 'block' }}>
                      {staleOpen.length === 1 ? 'An older copy' : `${staleOpen.length} older copies`} still open in
                      DocuSign. Void {staleOpen.length === 1 ? 'it' : 'them'} under Sent for signature.
                    </span>
                  );
                  if (newest && newest.state === 'completed') {
                    return (
                      <span className={page.itemMeta} style={{ maxWidth: 280 }}>
                        <span className={page.okText}>Signed by everyone{day(newest.completedAt) ? ` on ${day(newest.completedAt)}` : ''}.</span>{' '}
                        <button type="button" className={page.linkBtn} disabled={viewSigned.isPending}
                          onClick={() => viewSigned.mutate(newest.envelopeId)}>
                          View signed copy
                        </button>
                        {stale}
                      </span>
                    );
                  }
                  if (newest && isLive(newest)) {
                    return (
                      <span className={page.itemMeta} style={{ maxWidth: 280 }}>
                        Out for signature{newest.recipientEmail ? `, sent to ${newest.recipientEmail}` : ''}
                        {day(newest.sentAt) ? ` on ${day(newest.sentAt)}` : ''}.
                        {stale}
                      </span>
                    );
                  }
                  if (staleOpen.length > 0) return <span style={{ maxWidth: 280 }}>{stale}</span>;
                  const sendingKey = send.isPending ? send.variables : null;
                  const sendingLabel = sendingKey ? (pack.data?.documents ?? []).find((d) => d.key === sendingKey)?.envelopeLabel : null;
                  if (sendingKey && sendingKey !== doc.key && sendingLabel && sendingLabel === doc.envelopeLabel) {
                    return <span className={page.itemMeta}>Sending in the same envelope…</span>;
                  }
                  return confirming === doc.key ? (
                    <>
                      <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={busy}
                        onClick={() => send.mutate(doc.key)}>
                        {/*
                          * Name the documents. This said "Send both", which is a
                          * pronoun with no antecedent on a row labeled
                          * Subscription Agreement — it named neither document, so
                          * the only way to find out what the second one was, was
                          * to send it. `envelopeLabel` is built by the same helper
                          * that builds the DocuSign subject, so the button cannot
                          * promise something the envelope does not contain.
                          */}
                        {send.isPending ? 'Sending…'
                          : `Send ${doc.envelopeLabel ?? doc.label} to ${doc.recipientEmail}`}
                      </button>
                      <button type="button" className={page.btn} disabled={busy}
                        onClick={() => setConfirming(null)}>Cancel</button>
                    </>
                  ) : (
                    <button type="button" className={page.btn} disabled={busy || !doc.ready}
                      title={doc.ready ? undefined : 'Something is missing — see above'}
                      onClick={() => setConfirming(doc.key)}>
                      Send for signature…
                    </button>
                  );
                })()}
              </div>
            </li>
          );
        })}
      </ul>

      </GroupCard>

      {preview && (
        <GroupCard title={`${preview.label}, as ${(preview.documents?.length ?? 1) > 1 ? 'they' : 'it'} would be sent`}>
          <div className={page.headerRow}>
            {/*
              * "as they would be sent" when the envelope holds more than one.
              * The preview used to show only the clicked document while the
              * button sent two, so this heading was describing half of what
              * was about to go out.
              */}
            <div className={page.actions}>
              <button type="button" className={page.btn} disabled={busy}
                onClick={() => download.mutate(preview.document)}>
                {download.isPending ? 'Making the PDF…' : 'Download PDF'}
              </button>
              <button type="button" className={page.btn} onClick={() => setPreview(null)}>Close</button>
            </div>
          </div>
          {(preview.documents?.length ?? 1) > 1 && (
            <div className={page.itemMeta}>
              {preview.documents!.length} documents, one after the other, exactly as they go in the
              envelope — {preview.documents!.join(', then ')}. One signature covers them all.
            </div>
          )}
          {!preview.ready && (
            <div className={`${page.notice} ${page.noticeWarn}`}>
              Still has blanks in it. Every <code>{'{{NAME}}'}</code> below is something nothing has
              supplied, shown where it sits so you can see what it would say.
            </div>
          )}
          {/*
            * The document itself, not its source.
            *
            * `srcDoc` with the HTML the PDF is made from, in an iframe so the
            * document's own stylesheet cannot reach the console and the
            * console's cannot reach the document. Sandboxed with no
            * permissions at all: it is a page to look at, and there is nothing
            * in it that needs to run.
            *
            * On --paper in both themes: it is a picture of paper, and a PDF
            * does not invert.
            */}
          <iframe
            title={`${preview.label} as ${(preview.documents?.length ?? 1) > 1 ? 'they' : 'it'} would be sent`}
            srcDoc={preview.html}
            sandbox=""
            style={{
              // --paper, not a literal white: the token exists for precisely
              // this, and is white in both themes because a PDF does not invert.
              width: '100%', height: 620, background: 'var(--paper)',
              border: '1px solid var(--line)', borderRadius: 'var(--radius)',
              boxShadow: 'var(--shadow-paper)', display: 'block',
            }}
          />
          <p className={page.itemMeta} style={{ marginTop: 'var(--sp-2)' }}>
            This is the document itself, rendered from the same HTML the PDF is made from.
            Only the page breaks differ. Download the PDF to see those too.
          </p>
        </GroupCard>
      )}

      {(pack.data?.envelopes ?? []).length > 0 && (
        <GroupCard title="Sent for signature">
          {/*
            * Voided envelopes are hidden, not deleted: they are the record of
            * what was sent and withdrawn, and one link brings them back.
            */}
          {voidedCount > 0 && (
            <p className={page.itemMeta}>
              <button type="button" className={page.linkBtn} aria-expanded={showVoided}
                onClick={() => setShowVoided((v) => !v)}>
                {showVoided ? 'Hide voided' : `Show voided (${voidedCount})`}
              </button>
            </p>
          )}
          {shownEnvelopes.length === 0 ? (
            <p className={page.absent}>Nothing out for signature or signed. Voided envelopes are hidden.</p>
          ) : (
          <ul className={page.list} aria-label="Envelopes">
            {shownEnvelopes.map((envelope) => (
              <li key={envelope.envelopeId} className={page.listItem} style={{ flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div className={page.itemName}>
                    {envelope.label}
                    <span className={`${page.itemMeta} ${STATE_TONE[envelope.state] ?? ''}`}
                      style={{ fontWeight: 400 }}>
                      {' · '}{STATE_LABEL[envelope.state] ?? envelope.state}
                    </span>
                  </div>
                  <div className={page.itemMeta}>
                    To {envelope.recipientEmail}
                    {day(envelope.sentAt) ? ` · sent ${day(envelope.sentAt)}` : ''}
                    {day(envelope.completedAt) ? ` · signed ${day(envelope.completedAt)}` : ''}
                    {envelope.sentByEmail ? ` · by ${envelope.sentByEmail}` : ''}
                  </div>
                </div>
                <div className={page.actions} style={{ flexShrink: 0 }}>
                  {envelope.state === 'completed' && (
                    <button type="button" className={page.btn} disabled={viewSigned.isPending}
                      onClick={() => viewSigned.mutate(envelope.envelopeId)}>
                      {viewSigned.isPending && viewSigned.variables === envelope.envelopeId ? 'Opening…' : 'View signed copy'}
                    </button>
                  )}
                  <button type="button" className={page.btn} disabled={busy}
                    onClick={() => recheck.mutate(envelope.envelopeId)}>
                    {recheck.isPending && recheck.variables === envelope.envelopeId
                      ? 'Checking…' : 'Check status'}
                  </button>
                  {(envelope.state === 'sent' || envelope.state === 'delivered') && voiding !== envelope.envelopeId && (
                    <button type="button" className={page.btn} disabled={busy}
                      onClick={() => { setVoiding(envelope.envelopeId); setVoidReason('This copy is no longer needed.'); }}>
                      Void…
                    </button>
                  )}
                </div>
                {voiding === envelope.envelopeId && (
                  <div style={{ flexBasis: '100%' }}>
                    <label className={fields.label} htmlFor={`void-${envelope.envelopeId}`}>
                      Reason, which DocuSign sends to {envelope.recipientEmail ?? 'the recipient'}
                    </label>
                    <input id={`void-${envelope.envelopeId}`} className={fields.input} maxLength={200}
                      value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
                    <div className={page.actions} style={{ marginTop: 'var(--sp-2)' }}>
                      <button type="button" className={`${page.btn} ${page.dangerText}`}
                        disabled={busy || voidReason.trim().length < 3}
                        onClick={() => voidOne.mutate(envelope.envelopeId)}>
                        {voidOne.isPending ? 'Voiding…' : 'Void this envelope'}
                      </button>
                      <button type="button" className={page.btn} onClick={() => setVoiding(null)}>Keep it</button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
          )}
          <p className={page.itemMeta}>
            DocuSign tells us when something is signed, and an executed document settles its own
            paperwork row above. "Check status" asks DocuSign directly, for when you would rather
            not wait.
          </p>
        </GroupCard>
      )}
    </section>
  );
}
